// Tiny client-side search index. No deps — just a tokenized inverted
// index with field weighting and prefix expansion. Built once per
// catalog snapshot, kept in module scope so repeated callers reuse it.
//
// Designed to be cheap: ~5–10 KB of state for a typical catalog of a
// few hundred items. Token map is a Map<string, Map<itemId, score>>
// so a query of N tokens is N hash lookups + a tiny merge.

const SEARCHABLE_FIELDS = [
  // [field, weight]
  ["name", 8],
  ["sku", 7],
  ["tagline", 4],
  ["shortDescription", 2],
  ["description", 1],
  ["productLine", 3],
  ["intendedUse", 3],
  ["frameSystem", 2],
];

const STOP = new Set([
  "the","a","an","and","or","of","for","with","on","to","in","is","it","be",
]);

let _catalogRef = null;
let _index = null;
let _itemsById = null;

function tokenize(text) {
  if (!text) return [];
  const out = [];
  for (const raw of String(text).toLowerCase().split(/[^a-z0-9]+/)) {
    if (!raw) continue;
    if (STOP.has(raw)) continue;
    if (raw.length < 2) continue;
    out.push(raw);
  }
  return out;
}

function fieldText(item, field) {
  const v = item?.[field];
  if (v == null) return "";
  if (Array.isArray(v)) return v.join(" ");
  return String(v);
}

function build(items) {
  const idx = new Map();           // token -> Map<id, score>
  const prefixes = new Map();      // 3-char prefix -> Set<token>
  const itemsById = new Map();
  for (const it of items) {
    const id = it.id ?? it.sku;
    if (!id) continue;
    itemsById.set(id, it);
    for (const [field, weight] of SEARCHABLE_FIELDS) {
      const toks = tokenize(fieldText(it, field));
      for (const t of toks) {
        let m = idx.get(t);
        if (!m) { m = new Map(); idx.set(t, m); }
        m.set(id, (m.get(id) || 0) + weight);
        if (t.length >= 3) {
          const p = t.slice(0, 3);
          let s = prefixes.get(p);
          if (!s) { s = new Set(); prefixes.set(p, s); }
          s.add(t);
        }
      }
    }
  }
  return { idx, prefixes, itemsById };
}

function ensureIndex(items) {
  if (_catalogRef === items && _index) return _index;
  const built = build(items || []);
  _index = built;
  _itemsById = built.itemsById;
  _catalogRef = items;
  return built;
}

// Resolve a query token to a set of indexed tokens. We try exact match,
// then prefix expansion (typing "ass" matches "assault"), capped to keep
// the merge tight.
function expand(qToken, idx, prefixes) {
  const out = [];
  if (idx.has(qToken)) out.push(qToken);
  if (qToken.length >= 3) {
    const bucket = prefixes.get(qToken.slice(0, 3));
    if (bucket) {
      let n = 0;
      for (const t of bucket) {
        if (t === qToken) continue;
        if (t.startsWith(qToken)) { out.push(t); if (++n >= 8) break; }
      }
    }
  }
  return out;
}

export function search(items, query, { limit = 25 } = {}) {
  const q = String(query || "").trim();
  if (!q) return [];
  const { idx, prefixes, itemsById } = ensureIndex(items);
  const qTokens = tokenize(q);
  if (!qTokens.length) return [];

  const scores = new Map();          // id -> score
  const matchedTokenCount = new Map(); // id -> distinct query tokens matched

  for (const qt of qTokens) {
    const expanded = expand(qt, idx, prefixes);
    if (!expanded.length) continue;
    const seen = new Set();
    for (let i = 0; i < expanded.length; i++) {
      const t = expanded[i];
      const exactBoost = t === qt ? 2 : 1;
      const m = idx.get(t);
      if (!m) continue;
      for (const [id, w] of m) {
        scores.set(id, (scores.get(id) || 0) + w * exactBoost);
        if (!seen.has(id)) {
          seen.add(id);
          matchedTokenCount.set(id, (matchedTokenCount.get(id) || 0) + 1);
        }
      }
    }
  }

  // Reward results that hit *more* of the query — beats single-token spam.
  const ranked = [];
  for (const [id, score] of scores) {
    const coverage = (matchedTokenCount.get(id) || 1) / qTokens.length;
    ranked.push([id, score * (0.5 + coverage)]);
  }
  ranked.sort((a, b) => b[1] - a[1]);
  return ranked.slice(0, limit).map(([id]) => itemsById.get(id)).filter(Boolean);
}

export function suggest(items, query, { limit = 6 } = {}) {
  // Lightweight name-only suggestions for the dropdown — same index,
  // just filtered to results that match the prefix in the name.
  const hits = search(items, query, { limit: limit * 2 });
  const q = String(query || "").trim().toLowerCase();
  return hits
    .filter((it) => (it.name || "").toLowerCase().includes(q.split(/\s+/)[0] || ""))
    .slice(0, limit);
}

export function clearSearchIndex() {
  _catalogRef = null;
  _index = null;
  _itemsById = null;
}
